import React, { useState } from 'react';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonClass?: string;
  requireTextConfirmation?: boolean;
  textToConfirm?: string;
  warningItems?: string[];
  isLoading?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmButtonClass = "bg-[#dc2626] hover:bg-[#b91c1c]",
  requireTextConfirmation = false,
  textToConfirm = "",
  warningItems = [],
  isLoading = false
}) => {
  const [confirmationInput, setConfirmationInput] = useState('');

  if (!isOpen) return null;

  const isConfirmDisabled = requireTextConfirmation 
    ? confirmationInput !== textToConfirm || isLoading
    : isLoading;

  const handleConfirm = () => {
    if (!isConfirmDisabled) {
      onConfirm();
      setConfirmationInput(''); // Reset input
    }
  };

  const handleClose = () => {
    setConfirmationInput(''); // Reset input
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[85vh] overflow-y-auto shadow-[0_4px_12px_rgba(0,0,0,0.08)] border border-[#eaeaea]" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
        {/* Header — the title can be long (it often names an entity), so the
            icons hold their size and the text takes the remaining width. */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3 min-w-0">
            <FaExclamationTriangle className="mt-0.5 w-5 h-5 shrink-0 text-red-600" />
            <h3 className="text-[15px] font-semibold text-[#171717] break-words line-clamp-3" title={title}>{title}</h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            aria-label="Close"
            className="shrink-0 -mt-1 -mr-1 p-1.5 rounded text-[#aaa] hover:text-[#444] hover:bg-[#f5f5f5] transition-colors duration-150 cursor-pointer disabled:cursor-default"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="mb-6">
          <p className="text-[13px] text-[#444] mb-4 break-words">{message}</p>

          {/* Warning items */}
          {warningItems.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-[12px] text-red-800 font-medium mb-2">
                This action cannot be undone. This will permanently delete:
              </p>
              <ul className="text-[12px] text-red-700 ml-4">
                {warningItems.map((item, index) => (
                  <li key={index}>• {item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Text confirmation */}
          {requireTextConfirmation && (
            <div>
              <label htmlFor="confirmation-input" className="block text-[13px] font-medium text-[#444] mb-2">
                Please type <strong>{textToConfirm}</strong> to confirm:
              </label>
              <input
                id="confirmation-input"
                type="text"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                className="w-full px-3 py-2 text-[13px] border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 transition-colors duration-150"
                placeholder={`Type "${textToConfirm}" here`}
                disabled={isLoading}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-[13px] text-[#444] bg-[#fafafa] border border-[#eaeaea] rounded-lg hover:bg-[#f0f0f0] transition-colors duration-150 cursor-pointer disabled:opacity-50 disabled:cursor-default"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            className={`px-4 py-2 text-[13px] text-white rounded-lg transition-colors duration-150 cursor-pointer disabled:opacity-50 disabled:bg-[#aaa] disabled:cursor-default flex items-center space-x-2 ${confirmButtonClass}`}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent"></div>
                <span>Processing...</span>
              </>
            ) : (
              <span>{confirmText}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
